import { SECTIONS, type Section } from '../../../shared/audience';
import type { MemberDTO } from '../../../shared/dto/members';
import { compareIcelandic, matchesSearch } from '../../lib/search';

export type GroupKey = Section | 'other';

/** Directory order: T1, T2, B1, B2, conductor, musicians, then members without a section. */
export const GROUP_ORDER: readonly GroupKey[] = [...SECTIONS, 'other'];

export interface MemberGroup<M extends MemberDTO = MemberDTO> {
  key: GroupKey;
  members: M[];
}

/** Statuses never shown in the directory (deactivated / erased members). */
export function isListed(m: Pick<MemberDTO, 'status'>): boolean {
  return m.status !== 'inactive' && m.status !== 'erased';
}

export function sortMembers<M extends Pick<MemberDTO, 'fullName'>>(list: M[]): M[] {
  return [...list].sort((a, b) => compareIcelandic(a.fullName, b.fullName));
}

/** Filter by search tokens (name + title, accent-insensitive), group by section, sort each group. */
export function groupMembers<M extends MemberDTO>(list: readonly M[], tokens: readonly string[]): MemberGroup<M>[] {
  const buckets = new Map<GroupKey, M[]>();
  for (const m of list) {
    if (!isListed(m) || !matchesSearch(tokens, m.fullName, m.title)) continue;
    const key: GroupKey = m.section ?? 'other';
    const arr = buckets.get(key);
    if (arr) arr.push(m);
    else buckets.set(key, [m]);
  }
  return GROUP_ORDER.filter((k) => buckets.has(k)).map((key) => ({ key, members: sortMembers(buckets.get(key)!) }));
}
